import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { useSignOut } from './useSignOut';

export interface SessionValue {
  /** Display name from the signed-in account, before /api/me has answered. */
  accountName: string | null;
  accountUpn: string | null;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

/** Reads the active MSAL account once so the shell never needs MSAL hooks directly. */
export function SessionProvider({ children }: { children: ReactNode }) {
  const { instance } = useMsal();
  const signOut = useSignOut();
  const account = instance.getActiveAccount();
  const value = useMemo<SessionValue>(
    () => ({ accountName: account?.name ?? null, accountUpn: account?.username ?? null, signOut }),
    [account?.name, account?.username, signOut],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** Variant for tests and the dev mock harness. */
export function StaticSessionProvider({ value, children }: { value: SessionValue; children: ReactNode }) {
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
